import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { UI } from "@/i18n";
import { STATIC_CONTENT } from "@/data/content";

const LangContext = createContext(null);
const ContentContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

function resolve(dict, path) {
  return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), dict);
}

function mergeContent(remote) {
  // Merge remote (CMS) content over the verified static dataset, collection by
  // collection, so the site still renders fully when the backend is unreachable.
  if (!remote || typeof remote !== "object") return STATIC_CONTENT;
  const merged = { ...STATIC_CONTENT };
  for (const key of ["settings", "kpis", "case_studies", "experiences", "skills", "certifications"]) {
    const value = remote[key];
    const hasData =
      value != null &&
      (Array.isArray(value) ? value.length > 0 : typeof value === "object" && Object.keys(value).length > 0);
    if (hasData) merged[key] = value;
  }
  return merged;
}

export function AppProviders({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  const [content, setContent] = useState(() => mergeContent(null));

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang === "id" ? "id" : "en";
  }, [lang]);

  const t = useCallback(
    (path) => {
      const value = resolve(UI[lang], path);
      return value != null ? value : resolve(UI.en, path);
    },
    [lang]
  );

  const L = useCallback(
    (field) => {
      if (field == null) return "";
      if (typeof field === "string") return field;
      return field[lang] || field.en || field.id || "";
    },
    [lang]
  );

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/content`);
      const data = await res.json();
      setContent(mergeContent(data));
    } catch (e) {
      // Backend unreachable — keep serving the verified static dataset.
      setContent(mergeContent(null));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const langValue = useMemo(() => ({ lang, setLang, t, L }), [lang, t, L]);
  const contentValue = useMemo(() => ({ content, refresh }), [content, refresh]);

  return (
    <LangContext.Provider value={langValue}>
      <ContentContext.Provider value={contentValue}>{children}</ContentContext.Provider>
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
export const useContent = () => useContext(ContentContext);
